import React, { useEffect, useState } from "react";
import { fetchIngredients, createIngredient, updateIngredient, deleteIngredient, adjustIngredientStock, fetchSuppliers, getCurrency } from "../services/api";
import { formatPrice } from "../utils/currency";
import { useT } from "../i18n/useT";

const UNITS = ["kg", "g", "L", "ml", "pcs", "tin", "sack", "bottle"];

// F&B Cost Control — Ingredients master (the pantry). Manager / Controller.
export default function IngredientsPage() {
  const { t } = useT();
  const cur = getCurrency() || "ETB";
  const [items, setItems] = useState([]);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", unit: "kg", unit_cost: "", category: "" });
  const [saving, setSaving] = useState(false);
  const [suppliers, setSuppliers] = useState([]);

  const load = () => {
    setLoading(true);
    fetchIngredients().then(setItems).catch((e) => setErr(e?.response?.data?.detail || t("cc_could_not_load")))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { fetchSuppliers().then(setSuppliers).catch(() => setSuppliers([])); }, []);

  const add = async (e) => {
    e.preventDefault(); setErr("");
    if (!form.name.trim() || !form.unit.trim()) { setErr(t("cc_name_unit_required")); return; }
    setSaving(true);
    try {
      await createIngredient({ name: form.name.trim(), unit: form.unit.trim(), unit_cost: Number(form.unit_cost) || 0, category: form.category.trim() || undefined });
      setForm({ name: "", unit: "kg", unit_cost: "", category: "" });
      load();
    } catch (e2) { setErr(e2?.response?.data?.detail || t("cc_could_not_add")); }
    finally { setSaving(false); }
  };

  const saveCost = async (it, value) => {
    const v = Number(value);
    if (Number.isNaN(v) || v === it.unit_cost) return;
    try { await updateIngredient(it.id, { unit_cost: v }); setItems((cur2) => cur2.map((x) => x.id === it.id ? { ...x, unit_cost: v } : x)); }
    catch { /* ignore */ }
  };
  const saveStock = async (it, value) => {
    const v = Number(value);
    if (Number.isNaN(v) || v === it.stock_on_hand) return;
    try { const u = await adjustIngredientStock(it.id, { mode: "set", value: v }); setItems((c) => c.map((x) => x.id === it.id ? { ...x, stock_on_hand: u.stock_on_hand } : x)); }
    catch { /* ignore */ }
  };
  const saveReorder = async (it, value) => {
    const v = value === "" ? null : Number(value);
    if (v !== null && Number.isNaN(v)) return;
    if (v === (it.reorder_level ?? null)) return;
    try { await updateIngredient(it.id, { reorder_level: v }); setItems((c) => c.map((x) => x.id === it.id ? { ...x, reorder_level: v } : x)); }
    catch { /* ignore */ }
  };
  const savePar = async (it, value) => {
    const v = value === "" ? null : Number(value);
    if (v !== null && Number.isNaN(v)) return;
    if (v === (it.par_level ?? null)) return;
    try { await updateIngredient(it.id, { par_level: v }); setItems((c) => c.map((x) => x.id === it.id ? { ...x, par_level: v } : x)); }
    catch { /* ignore */ }
  };
  const saveSupplier = async (it, value) => {
    const v = value === "" ? null : Number(value);
    if (v === (it.preferred_supplier_id ?? null)) return;
    try { await updateIngredient(it.id, { preferred_supplier_id: v }); setItems((c) => c.map((x) => x.id === it.id ? { ...x, preferred_supplier_id: v } : x)); }
    catch { /* ignore */ }
  };
  const isLow = (it) => it.reorder_level != null && (it.stock_on_hand || 0) <= it.reorder_level;

  const remove = async (it) => {
    if (!window.confirm(t("cc_delete_confirm").replace("{name}", it.name))) return;
    await deleteIngredient(it.id); load();
  };

  return (
    <>
      <h1>🥕 {t("cc_nav_ingredients")}</h1>
      <p className="muted" style={{ marginTop: -4, maxWidth: 620 }}>
        {t("cc_ing_intro")}
      </p>

      <form className="card" onSubmit={add} style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end", padding: 12, maxWidth: 760 }}>
        <label style={{ fontSize: 12, color: "#5f6b78", flex: "2 1 160px" }}>{t("cc_name")}<br />
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("cc_ing_name_ph")} style={{ width: "100%" }} />
        </label>
        <label style={{ fontSize: 12, color: "#5f6b78", flex: "1 1 90px" }}>{t("cc_unit")}<br />
          <input list="units" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} style={{ width: "100%" }} />
          <datalist id="units">{UNITS.map((u) => <option key={u} value={u} />)}</datalist>
        </label>
        <label style={{ fontSize: 12, color: "#5f6b78", flex: "1 1 110px" }}>{t("cc_cost_per_unit")} ({cur})<br />
          <input type="number" min="0" step="0.01" value={form.unit_cost} onChange={(e) => setForm({ ...form, unit_cost: e.target.value })} style={{ width: "100%" }} />
        </label>
        <label style={{ fontSize: 12, color: "#5f6b78", flex: "1 1 120px" }}>{t("cc_category")}<br />
          <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder={t("cc_optional")} style={{ width: "100%" }} />
        </label>
        <button className="btn" type="submit" disabled={saving}>{saving ? t("cc_adding") : t("btn_add")}</button>
      </form>

      {err && <div className="error" style={{ marginTop: 12 }}>{err}</div>}
      {loading ? <p className="muted" style={{ marginTop: 12 }}>{t("loading")}</p> : (
        <div className="card" style={{ marginTop: 12, overflowX: "auto", maxWidth: 760 }}>
          {items.length === 0 ? <p className="muted">{t("cc_no_ingredients")}</p> : (
            <table className="data" style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr style={{ background: "#008080" }}>
                <th style={{ color: "#fff", textAlign: "left", padding: "8px 10px" }}>{t("cc_ingredient")}</th>
                <th style={{ color: "#fff", padding: "8px 10px" }}>{t("cc_unit")}</th>
                <th style={{ color: "#fff", textAlign: "right", padding: "8px 10px" }}>{t("cc_cost_per_unit")}</th>
                <th style={{ color: "#fff", textAlign: "right", padding: "8px 10px" }}>{t("cc_stock")}</th>
                <th style={{ color: "#fff", textAlign: "right", padding: "8px 10px" }}>{t("cc_reorder_at")}</th>
                <th style={{ color: "#fff", textAlign: "right", padding: "8px 10px" }}>{t("cc_par")}</th>
                <th style={{ color: "#fff", padding: "8px 10px" }}>{t("cc_supplier")}</th>
                <th style={{ color: "#fff", padding: "8px 10px" }}>{t("cc_category")}</th>
                <th style={{ color: "#fff", padding: "8px 10px" }}></th>
              </tr></thead>
              <tbody>
                {items.map((it, i) => (
                  <tr key={it.id} style={{ background: isLow(it) ? "#fdecec" : (i % 2 ? "#f2f9f8" : "#fff") }}>
                    <td style={{ padding: "6px 10px" }}><b>{it.name}</b></td>
                    <td style={{ padding: "6px 10px", textAlign: "center" }}>{it.unit}</td>
                    <td style={{ padding: "6px 10px", textAlign: "right" }}>
                      <input type="number" min="0" step="0.01" defaultValue={it.unit_cost}
                        onBlur={(e) => saveCost(it, e.target.value)} style={{ width: 90, textAlign: "right" }} />
                    </td>
                    <td style={{ padding: "6px 10px", textAlign: "right", whiteSpace: "nowrap" }}>
                      <input type="number" step="any" defaultValue={it.stock_on_hand}
                        onBlur={(e) => saveStock(it, e.target.value)}
                        style={{ width: 72, textAlign: "right", color: isLow(it) ? "#A32D2D" : "inherit", fontWeight: isLow(it) ? 700 : 400 }} />
                      {isLow(it) && <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: "#A32D2D" }}>LOW</span>}
                    </td>
                    <td style={{ padding: "6px 10px", textAlign: "right" }}>
                      <input type="number" step="any" defaultValue={it.reorder_level ?? ""} placeholder="—"
                        onBlur={(e) => saveReorder(it, e.target.value)} style={{ width: 64, textAlign: "right" }} />
                    </td>
                    <td style={{ padding: "6px 10px", textAlign: "right" }}>
                      <input type="number" step="any" defaultValue={it.par_level ?? ""} placeholder="—"
                        onBlur={(e) => savePar(it, e.target.value)} style={{ width: 64, textAlign: "right" }} title="Order-up-to level for suggested POs" />
                    </td>
                    <td style={{ padding: "6px 10px" }}>
                      <select defaultValue={it.preferred_supplier_id ?? ""} onChange={(e) => saveSupplier(it, e.target.value)} style={{ maxWidth: 130 }}>
                        <option value="">—</option>
                        {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                      </select>
                    </td>
                    <td style={{ padding: "6px 10px", color: "#5f6b78" }}>{it.category || "—"}</td>
                    <td style={{ padding: "6px 10px", textAlign: "right" }}>
                      <button className="btn secondary small" onClick={() => remove(it)}>{t("btn_delete")}</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </>
  );
}
